export interface JobCardActivityModuleStyle {
  /** Local image path under /images/icons/ or a Bootstrap Icon class (bi-*). */
  icon: string;
  /** Optional Bootstrap Icon fallback when a local image fails to load. */
  fallbackIcon?: string;
  color: string;
  bgClass: string;
}

const ICONS = {
  garage: "/images/icons/Garage_operations.png",
  estimation: "/images/icons/estimation.webp",
  approve: "/images/icons/approve_icon.png",
  jobCard: "/images/icons/job_card.webp",
  gatePass: "/images/icons/Gate%20pass.webp",
  manpower: "/images/icons/manpower.webp",
  invoice: "/images/icons/invoice.png",
  partsIssue: "/images/icons/Parts%20issue.png",
  partsReturn: "/images/icons/Parts%20return.png",
  stock: "/images/icons/Stock.png",
  sales: "/images/icons/sales.png",
  salesReturn: "/images/icons/sales-return-voucher.png",
  purchase: "/images/icons/purchase.png",
  customer: "/images/icons/Customer.webp",
  labour: "/images/icons/labour.png",
  vehicle: "/images/icons/vehicle.webp",
  masters: "/images/icons/masters.png",
} as const;

const proformaStyle: JobCardActivityModuleStyle = {
  icon: ICONS.invoice,
  fallbackIcon: "bi-file-earmark-ruled",
  color: "#0d9488",
  bgClass: "bg-teal-600",
};

const inventoryStyle: JobCardActivityModuleStyle = {
  icon: ICONS.partsIssue,
  fallbackIcon: "bi-boxes",
  color: "#22c55e",
  bgClass: "bg-green-500",
};

const accountsStyle: JobCardActivityModuleStyle = {
  icon: ICONS.sales,
  fallbackIcon: "bi-wallet2",
  color: "#10b981",
  bgClass: "bg-emerald-500",
};

export const JOB_CARD_ACTIVITY_MODULE_CONFIG: Record<string, JobCardActivityModuleStyle> = {
  Inspection: {
    icon: ICONS.garage,
    fallbackIcon: "bi-clipboard-check",
    color: "#3b82f6",
    bgClass: "bg-blue-500",
  },
  Estimate: {
    icon: ICONS.estimation,
    fallbackIcon: "bi-file-earmark-text",
    color: "#f97316",
    bgClass: "bg-orange-500",
  },
  "Job Card": {
    icon: ICONS.jobCard,
    fallbackIcon: "bi-card-checklist",
    color: "var(--color-primary-500, #35ba5b)",
    bgClass: "bg-primary-500",
  },
  Manpower: {
    icon: ICONS.manpower,
    fallbackIcon: "bi-people-fill",
    color: "var(--color-primary-600, #2ea04a)",
    bgClass: "bg-primary-600",
  },
  // Legacy rows (pre-split); keep for existing activity log entries
  Inventory: inventoryStyle,
  "Parts Request": {
    icon: ICONS.stock,
    fallbackIcon: "bi-box-seam",
    color: "#16a34a",
    bgClass: "bg-green-600",
  },
  "Parts Issue": inventoryStyle,
  "Parts Return": {
    icon: ICONS.partsReturn,
    fallbackIcon: "bi-arrow-return-left",
    color: "#15803d",
    bgClass: "bg-green-700",
  },
  Purchase: {
    icon: ICONS.purchase,
    fallbackIcon: "bi-cart-plus",
    color: "#0891b2",
    bgClass: "bg-cyan-600",
  },
  // Legacy rows (pre-split); keep for existing activity log entries
  Accounts: accountsStyle,
  "Manual Journal": {
    icon: ICONS.sales,
    fallbackIcon: "bi-journal-text",
    color: "#059669",
    bgClass: "bg-emerald-600",
  },
  "Petty Cash Voucher": {
    icon: ICONS.sales,
    fallbackIcon: "bi-cash",
    color: "#10b981",
    bgClass: "bg-emerald-500",
  },
  "Cash Payment Voucher": {
    icon: ICONS.sales,
    fallbackIcon: "bi-cash-stack",
    color: "#14b8a6",
    bgClass: "bg-teal-500",
  },
  "Contra Voucher": {
    icon: ICONS.sales,
    fallbackIcon: "bi-arrow-left-right",
    color: "#0f766e",
    bgClass: "bg-teal-700",
  },
  "Bank Payment Voucher": {
    icon: ICONS.invoice,
    fallbackIcon: "bi-bank",
    color: "#0d9488",
    bgClass: "bg-teal-600",
  },
  "Sales Return": {
    icon: ICONS.salesReturn,
    fallbackIcon: "bi-arrow-counterclockwise",
    color: "#0f766e",
    bgClass: "bg-teal-700",
  },
  "Credit Note": {
    icon: ICONS.salesReturn,
    fallbackIcon: "bi-file-earmark-minus",
    color: "#0f766e",
    bgClass: "bg-teal-700",
  },
  "Debit Note": {
    icon: ICONS.invoice,
    fallbackIcon: "bi-file-earmark-plus",
    color: "#115e59",
    bgClass: "bg-teal-800",
  },
  Proforma: proformaStyle,
  "Proforma Invoice": proformaStyle,
  Invoice: {
    icon: ICONS.invoice,
    fallbackIcon: "bi-receipt",
    color: "#6366f1",
    bgClass: "bg-indigo-500",
  },
  "JC Invoice Auth": {
    icon: ICONS.approve,
    fallbackIcon: "bi-shield-check",
    color: "#4f46e5",
    bgClass: "bg-indigo-600",
  },
  "Receipt Voucher": {
    icon: ICONS.sales,
    fallbackIcon: "bi-cash-coin",
    color: "#059669",
    bgClass: "bg-emerald-600",
  },
  QC: {
    icon: ICONS.masters,
    fallbackIcon: "bi-check-circle",
    color: "#06b6d4",
    bgClass: "bg-cyan-500",
  },
  "Gate Pass": {
    icon: ICONS.gatePass,
    fallbackIcon: "bi-door-open",
    color: "#64748b",
    bgClass: "bg-slate-500",
  },
  Customer: {
    icon: ICONS.customer,
    fallbackIcon: "bi-person-check",
    color: "#7e22ce",
    bgClass: "bg-purple-700",
  },
};

export const DEFAULT_ACTIVITY_MODULE_STYLE: JobCardActivityModuleStyle = {
  icon: ICONS.masters,
  fallbackIcon: "bi-circle-fill",
  color: "#64748b",
  bgClass: "bg-slate-400",
};

export function isImageIcon(icon: string): boolean {
  const s = (icon || "").trim();
  if (!s) return false;
  if (s.startsWith("bi-") || s.startsWith("bi ")) return false;
  return (
    s.startsWith("/") ||
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("data:") ||
    /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(s)
  );
}

export function bootstrapIconClass(icon: string): string {
  const s = (icon || "").trim();
  if (!s) return "bi bi-circle-fill";
  if (s.startsWith("bi ")) return s;
  if (s.startsWith("bi-")) return `bi ${s}`;
  return `bi bi-${s}`;
}

export function getModuleStyle(module: string): JobCardActivityModuleStyle {
  return JOB_CARD_ACTIVITY_MODULE_CONFIG[module] ?? DEFAULT_ACTIVITY_MODULE_STYLE;
}

export function statusBadgeClass(status: string): string {
  const s = (status || "").toLowerCase();
  if (s === "completed" || s === "ready" || s === "created") {
    return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300";
  }
  if (s === "edited" || s === "updated") {
    return "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300";
  }
  if (s === "allocated") {
    return "bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300";
  }
  if (s === "approved" || s === "authorized") {
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300";
  }
  if (s === "requested") {
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300";
  }
  if (s === "pending" || s === "wip" || s === "put on hold" || s.includes("hold")) {
    return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300";
  }
  if (s === "rejected") {
    return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
  }
  return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
}
